/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import React from "react";

import { getTheme } from "office-ui-fabric-react";
import styled from "styled-components";

import { LARGE_DESKTOP_WIDTH, MEDIA_BREAK } from "../../../Constants/Layout";
import ILayoutProps from "./Interfaces/ILayoutProps";
import ISpaceProps from "./Interfaces/ISpaceProps";

const theme = getTheme();

/**
 * Space to passed into any component.
 * @param props
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const Space = (props: ISpaceProps) => ({
  margin: props.margin || "",
  marginBottom: props.marginBottom || "",
  marginLeft: props.marginLeft || "",
  marginRight: props.marginRight || "",
  marginTop: props.marginTop || "",
  padding: props.padding || "",
  paddingBottom: props.paddingBottom || "",
  paddingLeft: props.paddingLeft || "",
  paddingRight: props.paddingRight || "",
  paddingTop: props.paddingTop || "",
});

/**
 * Flex-box designed div-row.
 * @component
 */
export const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

/**
 * Flex-box designed div-col.
 * @component
 */
export const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
`;

/**
 * Parent Wrapper with 100 left and right margins.
 */
export const LayoutWrapper = styled(FlexCol)<ILayoutProps & { backgroundColor?: string }>`
  padding-left: 83px;
  padding-right: 83px;
  max-width: 100vw;
  background-color: ${({ backgroundColor }): string =>
    backgroundColor ? theme.palette[backgroundColor] : ""};

  @media (min-width: ${LARGE_DESKTOP_WIDTH}px) {
    padding-left: 100px;
    padding-right: 100px;
  }
  @media (max-width: ${MEDIA_BREAK}px) {
    padding-left: 15px;
    padding-right: 15px;
  }
`;

/**
 * A parent flex column to any layout.
 * TODO: Remove this when editing home container.
 * @component
 */
export const LayoutFlexWrapper = styled(FlexCol)`
  grid-area: content;
  height: 100%;
  width: 100%;
  max-width: 100vw;
  background-color: ${theme.palette.white};
  overflow: hidden;
  z-index: ${1};
`;

/**
 * Flex-box designed div-col with props.
 * @component
 * @returns {FlexCol<ILayoutProps>}
 */
export const Column = styled(FlexCol)<ILayoutProps>`
  position: relative;
  flex: ${(props): ILayoutProps["flex"] => props.flex};
  flex-wrap: ${(props): ILayoutProps["flexWrap"] => props.flexWrap};
  width: ${(props): ILayoutProps["width"] => props.width};
  height: ${(props): ILayoutProps["height"] => props.height};
  text-align: ${(props): ILayoutProps["textAlign"] => props.textAlign};
  align-items: ${(props): ILayoutProps["alignItems"] => props.alignItems};
  align-self: ${(props): ILayoutProps["alignSelf"] => props.alignSelf};
  justify-content: ${(props): ILayoutProps["justifyContent"] => props.justifyContent};
  background-color: ${(props): ILayoutProps["backgroundColor"] => props.backgroundColor};
  @media (max-width: ${MEDIA_BREAK}px) {
    flex-direction: column;
  }
`;

/**
 * Flex-box designed div-col with props and additional space props.
 * @component
 * @returns {FlexCol<ILayoutProps>}
 */
export const ColumnContent = styled(({ ...rest }) => <Column {...rest} />)`
  ${Space}
`;

/**
 * Flex-box designed div-row with props.
 * @component
 * @returns {FlexRow<ILayoutProps>}
 */
export const Row = styled(FlexRow)<ILayoutProps>`
  position: relative;
  flex: ${(props): ILayoutProps["flex"] => props.flex};
  flex-wrap: ${(props): ILayoutProps["flexWrap"] => props.flexWrap};
  width: ${(props): ILayoutProps["width"] => props.width};
  height: ${(props): ILayoutProps["height"] => props.height};
  text-align: ${(props): ILayoutProps["textAlign"] => props.textAlign};
  align-items: ${(props): ILayoutProps["alignItems"] => props.alignItems};
  align-self: ${(props): ILayoutProps["alignSelf"] => props.alignSelf};
  justify-content: ${(props): ILayoutProps["justifyContent"] => props.justifyContent};
  background-color: ${(props): ILayoutProps["backgroundColor"] => props.backgroundColor};
  @media (max-width: ${MEDIA_BREAK}px) {
    flex-direction: column;
  }
`;

/**
 * Flex-box designed div-row with props and additional space props.
 * @component
 * @returns {FlexRow<ILayoutProps>}
 */
export const RowContent = styled(({ ...rest }) => <Row {...rest} />)`
  ${Space}
`;
